import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { clearCart } from '../_actions/cart.actions';
import { resetRates } from '../_actions/rates.actions';
import { removeVehicle } from '../_actions/vehicle.actions';
import { resetDealer } from '../_actions/dealer-actions';
import { resetMyAccount } from '../_actions/account.actions';
import { resetOktaUser } from '../_actions/okta.actions';
import { clearRegistration } from '../_actions/registration.actions';
import { removeToken } from '../_actions/token.actions';
import { resetReferenceID } from '../_actions/reference-id.actions';
import { sessionEnded } from '../_actions/session.actions';
import { resetPrismic } from '../_actions/prismic.actions';
import { guest, sdLogout } from '../_actions/app-flow-state.actions';
import { resetAbandonCart } from '../_actions/abandon-cart.actions';
import { avsReset } from '../_actions/avs-state.actions';

@Injectable({
  providedIn: 'root'
})
export class ReferrerSessionService {

  constructor(private store: Store) {}

  /**
   * Check if the current page load should reset session variables based on referrer
   * Only resets session if appFlowState contains 'serviceDrive' or is 'SDLogout'
   * @returns boolean indicating if session should be reset
   */
  shouldResetSession(): boolean {
    const referrer = document.referrer;
    const currentDomain = window.location.hostname;
    const appFlowState = sessionStorage.getItem('appFlowState');
    
    console.log('Referrer check - Current domain:', currentDomain);
    console.log('Referrer check - Document referrer:', referrer);
    console.log('Referrer check - App flow state:', appFlowState);
    
    // Only reset session if appFlowState contains 'serviceDrive' or is 'SDLogout'
    if (!appFlowState || (!appFlowState.includes('serviceDrive') && appFlowState !== 'SDLogout')) {
      console.log('App flow state does not contain serviceDrive - keeping session');
      return false;
    }
    
    // If no referrer, reset session (only for serviceDrive flow)
    if (!referrer) {
      console.log('No referrer found and in serviceDrive flow - resetting session');
      return true;
    }

    try {
      const referrerUrl = new URL(referrer);
      const referrerDomain = referrerUrl.hostname;
      
      console.log('Referrer check - Parsed referrer domain:', referrerDomain);
      
      // Check if referrer is from the same domain or subdomain
      if (this.isSameDomain(referrerDomain, currentDomain)) {
        console.log('Referrer is from same domain/subdomain - keeping session');
        return false;
      }
      
      // If referrer is from a different domain, reset session (only for serviceDrive flow)
      console.log('Referrer is from different domain and in serviceDrive flow - resetting session');
      return true;
    } catch (error) {
      console.error('Error parsing referrer URL:', error);
      // If we can't parse the referrer, reset session to be safe (only for serviceDrive flow)
      return true;
    }
  }

  /**
   * Check if two domains are the same or if one is a subdomain of the other
   * @param domain1 First domain to compare
   * @param domain2 Second domain to compare
   * @returns boolean indicating if domains are related
   */
  private isSameDomain(domain1: string, domain2: string): boolean {
    // Exact match
    if (domain1 === domain2) {
      return true;
    }
    
    // Check if one is a subdomain of the other
    const domain1Parts = domain1.split('.');
    const domain2Parts = domain2.split('.');
    
    // Get the main domain parts (last 2 parts for most domains)
    const domain1Main = domain1Parts.slice(-2).join('.');
    const domain2Main = domain2Parts.slice(-2).join('.');
    
    return domain1Main === domain2Main;
  }

  /**
   * Reset all session variables while preserving specific system keys
   * Only resets if appFlowState contains 'serviceDrive' or is 'SDLogout'
   */
  resetSessionVariables(): void {
    const appFlowState = sessionStorage.getItem('appFlowState');
    
    // Only reset session if appFlowState contains 'serviceDrive' or is 'SDLogout'
    if (!appFlowState || (!appFlowState.includes('serviceDrive') && appFlowState !== 'SDLogout')) {
      console.log('App flow state does not contain serviceDrive - skipping session reset');
      return;
    }
    
    console.log('Resetting session variables due to referrer policy (serviceDrive flow)');
    
    // Preserve specific system keys while clearing the rest
    const keysToPreserve = [
      'appFlowState', 
      'correlationId', 
      'lpTabId', 
      'pagesFlow', 
      'prismicEnv', 
      'robots', 
      'stickyHeader', 
      'urlParams',
      'preferredLanguage',
      'sessionTimeout'
    ];
    
    const preservedData: { [key: string]: string } = {};

    // Store the values we want to keep
    keysToPreserve.forEach(key => {
      const value = sessionStorage.getItem(key);
      if (value !== null) {
        preservedData[key] = value;
      }
    });

    // Clear all session storage
    sessionStorage.clear();

    // Restore the preserved keys
    Object.keys(preservedData).forEach(key => {
      sessionStorage.setItem(key, preservedData[key]);
    });

    // Manually clear additional session storage items that might not be handled by actions
    const additionalItemsToClear = [
      'lead', 'vehicle', 'rates', 'cart', 'registration', 'vinDecode', 'plan', 'email',
      'avs', 'tax', 'taxDP', 'taxMP', 'accountVerification', 'urlParams', 'confirmationNumber',
      'account', 'paymentHistory', 'paylink', 'refID', 'prismicPDF', 'productDisclaimers',
      'opp', 'vvc', 'sessionEnded', 'cookiesAccepted', 'paymentOptionsMax', 'oktaUser',
      'paypal', 'dealer', 'numOfMonthlyPayments', 'oldVehicle', 'odometerDocs', 'inspectionDocs',
      'paypalPickedAsOption', 'selectedPlan', 'termYears', 'termMileage', 'termTotalPrice',
      'monthlyPayments', 'subtotal', 'ppmActive', 'roadsideActive', 'ppaReq', 'presetProgram',
      'presetPlan', 'page', 'cartDeleteItem', 'code'
    ];

    additionalItemsToClear.forEach(item => {
      sessionStorage.removeItem(item);
    });

    // Reset all NgRx store states
    this.store.dispatch(clearCart());
    this.store.dispatch(resetRates());
    this.store.dispatch(removeVehicle());
    this.store.dispatch(resetDealer());
    this.store.dispatch(resetMyAccount());
    this.store.dispatch(resetOktaUser());
    this.store.dispatch(clearRegistration());
    this.store.dispatch(removeToken());
    this.store.dispatch(resetReferenceID());
    this.store.dispatch(sessionEnded());
    this.store.dispatch(resetPrismic());
    // Preserve SDLogout state if we're in SDLogout mode
    if (appFlowState === 'SDLogout') {
      this.store.dispatch(sdLogout());
    } else {
      this.store.dispatch(guest());
    }
    this.store.dispatch(resetAbandonCart());
    this.store.dispatch(avsReset());

    console.log('Session variables reset successfully');
  }

  /**
   * Store the current referrer for future reference
   */
  storeCurrentReferrer(): void {
    const referrer = document.referrer;
    if (referrer) {
      sessionStorage.setItem('lastReferrer', referrer);
      console.log('Stored referrer:', referrer);
    }
  }

  /**
   * Get the last stored referrer
   */
  getLastReferrer(): string | null {
    return sessionStorage.getItem('lastReferrer');
  }
}
